import { LitElement, css, html } from 'lit';
import { property, customElement, query } from 'lit/decorators.js';
import { resolveRouterPath } from '../router';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

import '@awesome.me/webawesome/dist/components/button/button.js';
import '@awesome.me/webawesome/dist/components/icon/icon.js';
import '@awesome.me/webawesome/dist/components/switch/switch.js';

const homePath = resolveRouterPath();

@customElement('app-header')
export class AppHeader extends LitElement {
  @property({ type: String }) title = 'Simple Tech Care';

  @property({ type: Boolean }) enableBack: boolean = false;

  // This drives both the switch position and the Light/Dark label in render().
  @property({ type: Boolean }) darkMode = false;

  private introTimeline?: gsap.core.Timeline;
  private scrollTrigger?: ScrollTrigger;

  @query('header') header!: HTMLElement;
  @query('header .brand') brand!: HTMLElement;
  @query('header .header-actions') headerActions!: HTMLElement;

  static styles = css`
    :host {
      display: block;
      flex: 1 1 auto;  //flex grow, shrink, and basis
      min-inline-size: 0;
    }

    header {
      align-items: center;
      box-sizing: border-box;
      background: var(--app-color-primary);
      color: var(--app-color-on-primary);
      display: flex;
      inline-size: 100%;
      justify-content: space-between;
      min-height: 56px;
      padding: 0 16px;
      position: relative;
      -webkit-app-region: drag;
    }

    header h1 {
      flex: 1 1 auto;
      margin: 0;
      font-size: 32px;
      font-family: 'Computer';
      min-inline-size: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    a {
      align-items: center;
      border-radius: 999px;
      display: flex;
      padding: 0.25em 0.5em;
      text-decoration: none;
      transition: background-color 150ms ease;
      -webkit-app-region: no-drag;
    }

    a:visited {
      color: var(--app-color-on-primary);
    }

    a:hover {
      background-color: color-mix(
        in srgb,
        var(--app-color-on-primary) 20%,
        transparent
      );
    }

    a:visited:hover {
      background-color: color-mix(
        in srgb,
        var(--app-color-on-primary) 32%,
        transparent
      );
    }

    img {
      height: 48px;
      width: auto;
    }

    .brand {
      align-items: center;
      display: flex;
      flex: 1 1 auto;
      gap: var(--wa-space-m);
      min-inline-size: 0;
    }

    .theme-control {
      align-items: center;
      display: flex;
      gap: var(--wa-space-xs);
      -webkit-app-region: no-drag;
    }

    .header-actions {
      align-items: center;
      display: flex;
      flex: 0 0 auto;
      gap: var(--wa-space-s);
      -webkit-app-region: no-drag;
    }

    ::slotted([slot='navigation-toggle']) {
      display: none;
    }

    @media (max-width: 599px) {
      ::slotted([slot='navigation-toggle']) {
        display: inline-flex;
      }

      header h1 {
        font-size: clamp(1rem, 4.5vw, 1.25rem);
      }

    }

    /* The header is brand-colored, so the default checked track can blend in. */
    wa-switch::part(control) {
      background-color: var(--wa-color-surface-default);
      border-color: var(--wa-color-surface-default);
    }

    wa-switch::part(thumb) {
      background-color: var(--wa-color-brand-fill-loud);
    }

    wa-switch::part(control):focus-within {
      outline: var(--app-focus-ring);
      outline-offset: var(--app-focus-offset);
    }

    @media (max-width: 500px) {
      .theme-control span {
        display: none;
      }
    }
  `;

  connectedCallback() {
    super.connectedCallback();
    // Match the mode selected on a previous visit before rendering the switch.
    const savedScheme = localStorage.getItem('color-scheme');
    // A saved choice wins; otherwise begin with the operating system preference.
    this.darkMode = savedScheme
      ? savedScheme === 'dark'
      : window.matchMedia('(prefers-color-scheme: dark)').matches;
    this.applyColorScheme();
  }

  private handleThemeChange(event: Event) {
    // wa-switch emits the native change event after its checked property updates.
    this.darkMode = (
      event.currentTarget as HTMLElement & { checked: boolean }
    ).checked;
    this.applyColorScheme();
  }

  firstUpdated(): void {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return;
    }

    const tl = gsap.timeline();
    this.introTimeline = tl;
    tl.from(this.header, {
      autoAlpha: 0,
      y: -100,
      duration: 1.5,
      ease: 'expo.out',
    })
      .from(
        this.brand,
        {
          autoAlpha: 0,
          // y: -100,
          scale: 0.7,
          transformOrigin: 'left center',
          duration: 2.5,
          ease: 'elastic.out(1,0.3)',
        },
        '-=0.9'
      )
      .from(
        this.headerActions,
        {
          autoAlpha: 0,
          scale: 0.8,
          duration: 2,
          ease: 'elastic.out(1,0.3)',
        },
        '<'
      );

    // Replay the intro animation each time the user starts scrolling up.
    let lastDirection = 1;
    this.scrollTrigger = ScrollTrigger.create({
      start: 0,
      end: 'max',
      onUpdate: (self) => {
        if (self.direction === -1 && lastDirection !== -1 && self.scroll() > 0) {
          tl.restart();
        }
        lastDirection = self.direction;
      },
    });
  }

  disconnectedCallback() {
    this.scrollTrigger?.kill();
    this.introTimeline?.kill();
    super.disconnectedCallback();
  }

  private applyColorScheme() {
    const colorScheme = this.darkMode ? 'dark' : 'light';
    const root = document.documentElement;
    // Web Awesome themes use these root classes, not color-scheme alone.
    root.classList.toggle('wa-dark', this.darkMode);
    root.classList.toggle('wa-light', !this.darkMode);
    // This also updates native browser controls and light-dark() CSS values.
    root.style.colorScheme = colorScheme;
    localStorage.setItem('color-scheme', colorScheme);
  }

  render() {
    return html`
      <header>
        <div class="brand">
          <a href="${homePath}" aria-label="${this.title}">
            <img src="/images/circle.png" alt="${this.title} logo" />
          </a>
          <h1 id="site-title">${this.title}</h1>
        </div>
        <div class="header-actions">
          <div class="theme-control">
            <span>${this.darkMode ? 'Dark Mode' : 'Light Mode'}</span>
            <wa-switch
              size="xl"
              ?checked=${this.darkMode}
              aria-label="Use ${this.darkMode ? 'light' : 'dark'} mode"
              @change=${this.handleThemeChange}
            ></wa-switch>
          </div>
          <slot name="navigation-toggle"></slot>
        </div>
      </header>
    `;
  }
}
